import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { CreateCalculationRunAccrual, UpdateCalculationRunAccrual } from '@repo/backend/crt'
import { CALCULATION_RUN_ACCRUALS_CREATE_ERROR_TYPES, PRODUCT_CATEGORIES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const calculationRunAccrualsSearchSchema = z.object({
  keyword: z.string().optional(),
  period: monthStartString.optional(),
  ...datatableSearchSchema.shape,
})

export const calculationRunAccrualsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  budgetAllocation: z.array(z.string()).optional(),
  conditionType: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  hierarchyErp: z.array(z.string()).optional(),
  masterAgreement: z.array(z.string()).optional(),
  agreementNumber: z.array(z.string()).optional(),
  investmentType: z.array(z.string()).optional(),
  billingDateFrom: z.coerce.date().optional(),
  billingDateTo: z.coerce.date().optional(),
  ...datatableSearchSchema.shape,
})

export type CalculationRunAccrualsSearch = z.infer<typeof calculationRunAccrualsSearchSchema>
export type CalculationRunAccrualsDetailsSearch = z.infer<
  typeof calculationRunAccrualsDetailsSearchSchema
>

export type CalculationRunAccrualsList = Awaited<ReturnType<typeof getCalculationRunAccrualsList>>
export type CalculationRunAccrualListItem = CalculationRunAccrualsList['results'][number]
export type CalculationRunAccrualWithDetails = Awaited<
  ReturnType<typeof getCalculationRunAccrualById>
>

type UseCreateCalculationRunAccrualArgs = {
  queries: CalculationRunAccrualsSearch
  handleOnSuccess: (calculationRunAccrualId: number) => void
}

type UseUpdateCalculationRunAccrualArgs = QueryHandlers & {
  calculationRunAccrualId: number
}

type UseDeleteCalculationRunAccrualsArgs = QueryHandlers & {
  calculationRunAccrualIds: number[]
  queries: CalculationRunAccrualsSearch
}

async function getCurrentCalculationRunAccrual(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const query = crtPeriod ? { crtPeriod } : {}
  const res = await api.crt['calculation-run-accruals'].current.$get(
    { query },
    { init: { signal } },
  )
  return await res.json()
}

export const currentCalculationRunAccrualQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentCalculationRunAccrual', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCurrentCalculationRunAccrual(crtPeriod, signal),
})

export function useCurrentCalculationRunAccrual(crtPeriod?: string) {
  return useQuery(currentCalculationRunAccrualQueryOptions(crtPeriod))
}

/**
 * The import holding one period — its month of billing — or `null`. Detail pages are addressed by that
 * period, so their loader resolves the import's id from it through the list endpoint's period filter.
 */
export const calculationRunAccrualByPeriodQueryOptions = (period: string) => ({
  queryKey: ['calculationRunAccrualByPeriod', period] as const,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const { results } = await getCalculationRunAccrualsList(
      { period, page: 1, pageSize: 1 },
      signal,
    )
    return results[0] ?? null
  },
})

async function getLatestCalculationRunAccrual(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['calculation-run-accruals'].latest.$get({}, { init: { signal } })
  return await res.json()
}

export function useLatestCalculationRunAccrual() {
  return useQuery({
    queryKey: ['latestCalculationRunAccrual'] as const,
    queryFn: ({ signal }) => getLatestCalculationRunAccrual(signal),
  })
}

async function getCalculationRunAccrualsList(
  search: CalculationRunAccrualsSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['calculation-run-accruals'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getCalculationRunAccrualById(
  id: string,
  queries: CalculationRunAccrualsDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const res = await api.crt['calculation-run-accruals'][':id'].$get(
    {
      param: { id },
      query: stringifyValues({
        ...rest,
        billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
        billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
      }),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createCalculationRunAccrual(payload: CreateCalculationRunAccrual) {
  const api = getApi()

  const res = await api.crt['calculation-run-accruals'].$post({
    json: payload,
  })

  return await res.json()
}

async function updateCalculationRunAccrual(
  calculationRunAccrualId: number,
  details: UpdateCalculationRunAccrual,
) {
  const api = getApi()

  await api.crt['calculation-run-accruals'][':id'].$patch({
    param: { id: String(calculationRunAccrualId) },
    json: details,
  })
}

async function deleteCalculationRunAccruals(calculationRunAccrualIds: number[]) {
  const api = getApi()

  await api.crt['calculation-run-accruals'].bulk.$delete({
    json: { ids: calculationRunAccrualIds },
  })
}

export function useCalculationRunAccrualsList(queries: CalculationRunAccrualsSearch) {
  return useQuery({
    queryKey: ['calculationRunAccruals', queries],
    queryFn: ({ signal }) => getCalculationRunAccrualsList(queries, signal),
  })
}

export const calculationRunAccrualByIdQueryOptions = (
  id: string,
  queries: CalculationRunAccrualsDetailsSearch = {},
) => ({
  queryKey: ['calculationRunAccrualById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCalculationRunAccrualById(id, queries, signal),
})

export function useCalculationRunAccrualById(
  id: string,
  queries: CalculationRunAccrualsDetailsSearch = {},
) {
  return useQuery(calculationRunAccrualByIdQueryOptions(id, queries))
}

async function getCalculationRunAccrualDetailsForExport(
  id: string,
  queries: CalculationRunAccrualsDetailsSearch = {},
  signal?: AbortSignal,
) {
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const stringifiedQueries = stringifyValues({
    ...rest,
    billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
    billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
  })

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/calculation-run-accruals/${id}/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export const calculationRunAccrualDetailsExportQueryOptions = (
  id: string,
  queries: CalculationRunAccrualsDetailsSearch = {},
) => ({
  queryKey: ['calculationRunAccrualDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCalculationRunAccrualDetailsForExport(id, queries, signal),
})

async function getCalculationRunAccrualDetailsForRawExport(id: string, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(`/crt/calculation-run-accruals/${id}/raw-export`, signal)
  return csvExport
}

export const calculationRunAccrualDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['calculationRunAccrualDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCalculationRunAccrualDetailsForRawExport(id, signal),
})

export function useCreateCalculationRunAccrual({
  queries,
  handleOnSuccess,
}: UseCreateCalculationRunAccrualArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['calculationRunAccruals', queries],
    mutationFn: createCalculationRunAccrual,
    onSuccess: (calculationRunAccrual) => {
      handleOnSuccess(calculationRunAccrual.id)

      queryClient.invalidateQueries({
        queryKey: ['calculationRunAccruals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('calculationRunAccruals.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; period?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case CALCULATION_RUN_ACCRUALS_CREATE_ERROR_TYPES.periodConflict:
          description = `${t('errors.calculationRunAccruals.createFailed', { ns: 'crt' })}: ${t(
            'errors.calculationRunAccruals.periodConflict',
            { ns: 'crt', period: customParams.period },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(
            issues,
            t,
            'errors.calculationRunAccruals.createFailed',
            'crt',
          )
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateCalculationRunAccrual({
  calculationRunAccrualId,
  handleOnSuccess,
}: UseUpdateCalculationRunAccrualArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateCalculationRunAccrual', calculationRunAccrualId],
    mutationFn: (details: UpdateCalculationRunAccrual) =>
      updateCalculationRunAccrual(calculationRunAccrualId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['calculationRunAccrualById', String(calculationRunAccrualId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('calculationRunAccruals.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.calculationRunAccruals.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteCalculationRunAccruals({
  calculationRunAccrualIds,
  queries,
  handleOnSuccess,
}: UseDeleteCalculationRunAccrualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteCalculationRunAccruals'],
    mutationFn: () => deleteCalculationRunAccruals(calculationRunAccrualIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['calculationRunAccruals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('calculationRunAccruals.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.calculationRunAccruals.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
